import React, { useEffect, useState } from 'react';
import { XIcon, CheckCircleIcon, ZapIcon } from 'lucide-react';
// REPLACE THIS URL WITH YOUR ACTUAL N8N WEBHOOK URL
const WEBHOOK_URL = 'https://swedenmark.app.n8n.cloud/webhook/sales-lead-demo';
interface DemoModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function DemoModal({
  isOpen,
  onClose
}: DemoModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    aboutYou: '',
    consent: false
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    phone?: string;
  }>({});
  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };
  const validatePhone = (phone: string) => {
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15;
  };
  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setStatus('idle');
      setFieldErrors({});
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        aboutYou: '',
        consent: false
      });
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      id,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [id]: type === 'checkbox' ? checked : value
    }));
    // Clear field error on change
    if (id === 'email' || id === 'phone') {
      setFieldErrors(prev => ({
        ...prev,
        [id]: undefined
      }));
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate before submitting
    const errors: {
      email?: string;
      phone?: string;
    } = {};
    if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (formData.phone && !validatePhone(formData.phone)) {
      errors.phone = 'Please enter a valid phone number (at least 10 digits)';
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setStatus('submitting');
    try {
      const res = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          aboutYou: formData.aboutYou,
          source: 'sales-demo-request',
          timestamp: new Date().toISOString()
        })
      });
      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Webhook submission error:', error);
      setStatus('error');
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-145">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" onClick={onClose} aria-hidden="true" data-id="element-146" />

      {/* Modal Card */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]" role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-147">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50" data-id="element-148">
          <div className="flex items-center gap-2 text-blue-600" data-id="element-149">
            <ZapIcon size={20} data-id="element-150" />
            <h2 id="modal-title" className="text-lg font-bold text-gray-900" data-id="element-151">
              Try a Live Demo
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100" aria-label="Close modal" data-id="element-152">
            <XIcon size={20} data-id="element-153" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" data-id="element-154">
          {status === 'success' ? <div className="flex flex-col items-center justify-center text-center py-8 gap-4" data-id="element-155">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-2" data-id="element-156">
                <CheckCircleIcon size={36} className="text-green-600" data-id="element-157" />
              </div>
              <h3 className="text-xl font-bold text-gray-900" data-id="element-158">
                Demo Triggered!
              </h3>
              <p className="text-gray-600" data-id="element-159">
                Check your inbox and phone, {formData.name.split(' ')[0]}! Our
                AI automation is crafting a personalized response for you right
                now. This is exactly the kind of outreach we build for our
                clients.
              </p>
              <button onClick={onClose} className="mt-6 px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition-colors" data-id="element-160">
                Close Window
              </button>
            </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-161">
              <p className="text-sm text-gray-600 mb-6" data-id="element-162">
                Experience our AI automation firsthand. Hit submit and you'll
                immediately receive a personalized email and/or SMS — powered by
                the same AI workflows we build for clients.
              </p>

              <div data-id="element-163">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-164">
                  Full Name
                </label>
                <input type="text" id="name" value={formData.name} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" placeholder="Jane Doe" required data-id="element-165" />
              </div>

              <div data-id="element-166">
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-167">
                  Company Name
                </label>
                <input type="text" id="company" value={formData.company} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" placeholder="Acme Inc." data-id="element-168" />
              </div>

              <div data-id="element-169">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-170">
                  Email
                </label>
                <input type="email" id="email" value={formData.email} onChange={handleChange} className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors ${fieldErrors.email ? 'border-red-400 bg-red-50/50' : 'border-gray-300'}`} placeholder="jane@company.com" required data-id="element-171" />
                {fieldErrors.email && <p className="text-xs text-red-500 mt-1" data-id="element-172">
                    {fieldErrors.email}
                  </p>}
              </div>

              <div data-id="element-173">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-174">
                  Phone Number
                </label>
                <input type="tel" id="phone" value={formData.phone} onChange={handleChange} className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors ${fieldErrors.phone ? 'border-red-400 bg-red-50/50' : 'border-gray-300'}`} placeholder="(555) 000-0000" data-id="element-175" />
                {fieldErrors.phone && <p className="text-xs text-red-500 mt-1" data-id="element-176">
                    {fieldErrors.phone}
                  </p>}
              </div>

              <div data-id="element-177">
                <label htmlFor="aboutYou" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-178">
                  Tell us about yourself
                </label>
                <textarea id="aboutYou" value={formData.aboutYou} onChange={handleChange} rows={3} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors resize-none" placeholder="e.g. I'm a marketing director who spends too much time on spreadsheets and not enough time on my sourdough starter..." data-id="element-179" />
                <p className="text-xs text-gray-400 mt-1.5" data-id="element-180">
                  This will be used as sample input for the automation — the
                  more detail, the more personalized your demo response will be.
                </p>
              </div>

              <div className="flex items-start gap-3 pt-2" data-id="element-181">
                <div className="flex items-center h-5 mt-0.5" data-id="element-182">
                  <input id="consent" type="checkbox" checked={formData.consent} onChange={handleChange} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer" required data-id="element-183" />
                </div>
                <label htmlFor="consent" className="text-xs text-gray-500 leading-relaxed cursor-pointer" data-id="element-184">
                  By submitting, you agree to receive emails and text messages
                  from Swedenmark AI regarding your demo request and related
                  services. Message and data rates may apply. You can opt out at
                  any time by replying STOP.
                </label>
              </div>

              {status === 'error' && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-id="element-185">
                  Something went wrong connecting to the server. Please try
                  again.
                </div>}

              <div className="flex gap-3 pt-4 border-t border-gray-100 mt-6" data-id="element-186">
                <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium rounded-lg transition-colors" disabled={status === 'submitting'} data-id="element-187">
                  Cancel
                </button>
                <button type="submit" disabled={status === 'submitting'} className={`flex-1 px-4 py-2.5 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 ${status === 'submitting' ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 shadow-sm'}`} data-id="element-188">
                  {status === 'submitting' ? 'Sending...' : <>
                      Launch Demo <ZapIcon size={16} data-id="element-189" />
                    </>}
                </button>
              </div>
            </form>}
        </div>
      </div>
    </div>;
}